import React, { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import { TextField } from "@outpave/ds";
import type { TextFieldProps } from "@outpave/ds";
import { getCurrencySymbol } from "utils/formatCash";

interface CurrencyInputProps extends Omit<
  TextFieldProps,
  "value" | "onChange" | "leading" | "ref"
> {
  value: number;
  currency?: string; // e.g. "USD", "EUR"
  locale?: string; // e.g. "en-US", "es-ES"
  onChange: (value: number) => void;
  onBlur?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
}

/** Two decimals, grouped, whenever the field is not being typed in. */
const asMoney = (value: number, locale: string) =>
  isNaN(value)
    ? ""
    : value.toLocaleString(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

/**
 * A money field: grouped and two-decimal while idle, raw while typing, and
 * reporting a number rather than a string.
 *
 * The currency symbol goes in the package `TextField`'s `leading` slot, which
 * owns the start adornment. That slot is why this takes the DS field rather
 * than MUI's: the symbol used to be a bare `span` with an inline margin,
 * positioned by hand inside `slotProps.input`.
 */
const CurrencyInput: React.FC<CurrencyInputProps> = ({
  value,
  currency = "USD",
  locale = "en-US",
  onChange,
  onBlur,
  ...props
}) => {
  const [displayValue, setDisplayValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  // Format value for display unless editing
  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(asMoney(Number(value), locale));
    }
  }, [value, locale, isFocused]);

  return (
    <TextField
      {...props}
      // On the inner input, not the root: the field spreads unknown props to
      // its wrapper, and a numeric keypad has to be asked for on the input.
      inputProps={{ ...props.inputProps, inputMode: "decimal" }}
      leading={
        <Typography component="span" variant="inherit">
          {getCurrencySymbol(currency, locale)}
        </Typography>
      }
      value={displayValue}
      onFocus={() => {
        setIsFocused(true);
        // Remove formatting for editing
        setDisplayValue(displayValue.replace(/,/g, ""));
      }}
      onBlur={(event) => {
        setIsFocused(false);
        setDisplayValue(asMoney(Number(displayValue), locale));
        onBlur && onBlur(event);
      }}
      onChange={(e) => {
        const input = e.target;
        let raw = input.value.replace(/[^0-9.]/g, "");
        // Allow only one decimal point
        const parts = raw.split(".");
        if (parts.length > 2) {
          raw = parts[0] + "." + parts.slice(1).join("");
        }
        // Limit to two decimal places
        if (raw.includes(".")) {
          const [whole, decimal] = raw.split(".");
          raw = whole + "." + decimal.slice(0, 2);
        }
        setDisplayValue(raw);
        onChange(Number(raw));
      }}
    />
  );
};

export default CurrencyInput;
